// frontend/src/pages/WeeklyReports/WeeklyReports.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, LinearProgress,
  CircularProgress, Alert, Divider, IconButton, Collapse
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Remove, Warning, CheckCircle,
  Refresh, ExpandMore, ExpandLess, CalendarToday, School,
  AccessTime, LocalFireDepartment, EmojiEvents, Lightbulb
} from '@mui/icons-material';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';
import api from '../../services/api';

const trendIcons = {
  up: <TrendingUp fontSize="small" sx={{ color: '#4caf50' }} />,
  down: <TrendingDown fontSize="small" sx={{ color: '#f44336' }} />,
  stable: <Remove fontSize="small" sx={{ color: '#9e9e9e' }} />
};

function StatCard({ label, value, trend, suffix, prevValue }) {
  return (
    <Box sx={{ flex: 1, minWidth: 100, p: 1.5, borderRadius: 1, bgcolor: 'background.default', textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}{suffix}</Typography>
        {trend && trendIcons[trend]}
      </Box>
      {prevValue !== undefined && prevValue !== null && (
        <Typography variant="caption" color="text.secondary">Last week: {prevValue}{suffix}</Typography>
      )}
    </Box>
  );
}

function ChildReport({ report }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card sx={{ mb: 2, ...(report.needsAttention ? { border: '2px solid #ff9800' } : {}) }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School sx={{ color: '#2196f3' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{report.childName}</Typography>
              <Typography variant="caption" color="text.secondary">
                @{report.childUsername} {report.grade ? ` \u00B7 Grade ${report.grade}` : ''}
              </Typography>
            </Box>
            {report.needsAttention && (
              <Chip icon={<Warning />} label="Needs attention" size="small" color="warning" variant="outlined" />
            )}
          </Box>
          <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>

        <Collapse in={expanded}>
          {/* Attention alerts */}
          {report.needsAttention && report.attentionReasons.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
              {report.attentionReasons.map((reason, i) => (
                <Typography key={i} variant="body2">{reason}</Typography>
              ))}
            </Alert>
          )}

          {/* Stats row */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <StatCard label="Questions" value={report.questionsThisWeek} trend={report.questionsTrend}
              prevValue={report.questionsLastWeek} />
            <StatCard label="Accuracy" value={report.accuracyThisWeek != null ? report.accuracyThisWeek : '-'}
              suffix={report.accuracyThisWeek != null ? '%' : ''} trend={report.accuracyTrend}
              prevValue={report.accuracyLastWeek} />
            <StatCard label="Study Time"
              value={report.studyMinutesThisWeek >= 60 ? Math.round(report.studyMinutesThisWeek / 60) : report.studyMinutesThisWeek}
              suffix={report.studyMinutesThisWeek >= 60 ? 'h' : 'min'} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <StatCard label="Streak" value={report.currentStreak} suffix=" days" />
            <StatCard label="Active Goals" value={report.activeGoals} />
            <StatCard label="Goals Achieved" value={report.achievedGoalsThisWeek} />
          </Box>

          {/* Topics practised */}
          {report.topicsPractised.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Topics Practised</Typography>
              {report.topicsPractised.map(tp => (
                <Box key={tp.topic} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{tp.topic}</Typography>
                  <Chip label={`${tp.questionsAnswered}q`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  <Box sx={{ width: 80 }}>
                    <LinearProgress variant="determinate" value={tp.accuracy}
                      sx={{ height: 6, borderRadius: 3 }}
                      color={tp.accuracy >= 70 ? 'success' : tp.accuracy >= 50 ? 'warning' : 'error'} />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, width: 32, textAlign: 'right' }}>
                    {tp.accuracy}%
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Recommendation */}
          {report.recommendation && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: '#e8f5e9', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Lightbulb sx={{ color: '#4caf50', mt: 0.2 }} fontSize="small" />
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>{report.recommendation}</Typography>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

function WeeklyReportsContent() {
  const [latestReport, setLatestReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState(null);

  const fetchLatest = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/reports/weekly/latest');
      if (res.data.success) setLatestReport(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/api/reports/weekly/history?limit=10');
      if (res.data.success) setHistory(res.data.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const handleGenerate = async () => {
    try {
      setGenerating(true); setError(null);
      const res = await api.post('/api/reports/weekly/generate');
      if (res.data.success) { setLatestReport(res.data.data); setSelectedReport(null); }
    } catch (err) { setError(err.response?.data?.error || 'Failed to generate report'); }
    finally { setGenerating(false); }
  };

  const handleSelectHistorical = async (reportId) => {
    try {
      const res = await api.get(`/api/reports/weekly/${reportId}`);
      if (res.data.success) setSelectedReport(res.data.data);
    } catch (err) { console.error(err); }
  };

  const handleShowHistory = () => {
    if (!showHistory) fetchHistory();
    setShowHistory(!showHistory);
  };

  if (loading) return (<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>);

  const displayReport = selectedReport || latestReport;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Weekly Reports</Typography>
          <Typography variant="body2" color="text.secondary">Track your children's learning progress</Typography>
        </Box>
        <Button variant="outlined" size="small"
          startIcon={generating ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generating...' : 'Generate Now'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!displayReport ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <CalendarToday sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No reports yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Weekly reports are generated every Monday for Family-tier parents with linked children. You can also generate one now.
            </Typography>
            <Button variant="contained" startIcon={<Refresh />} onClick={handleGenerate} disabled={generating}>
              Generate First Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Report header */}
          <Card sx={{ mb: 2, bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Week of {new Date(displayReport.weekStarting).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })} &ndash;{' '}
                    {new Date(displayReport.weekEnding).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {displayReport.childReports.length} child{displayReport.childReports.length !== 1 ? 'ren' : ''}
                    {displayReport.childReports.some(r => r.needsAttention) &&
                      ` \u00B7 ${displayReport.childReports.filter(r => r.needsAttention).length} needs attention`}
                  </Typography>
                </Box>
                {selectedReport && (
                  <Button size="small" variant="text" onClick={() => setSelectedReport(null)}>
                    Back to latest
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Child reports */}
          {displayReport.childReports.map(cr => (
            <ChildReport key={cr.childId} report={cr} />
          ))}
        </>
      )}

      {/* History section */}
      <Box sx={{ mt: 3 }}>
        <Button variant="text" onClick={handleShowHistory} endIcon={showHistory ? <ExpandLess /> : <ExpandMore />}>
          {showHistory ? 'Hide past reports' : 'View past reports'}
        </Button>

        <Collapse in={showHistory}>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No past reports</Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              {history.map(h => (
                <Card key={h._id} sx={{ mb: 1, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                  onClick={() => handleSelectHistorical(h._id)}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        {new Date(h.weekStarting).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })} &ndash;{' '}
                        {new Date(h.weekEnding).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {h.childReports.map(cr => (
                          <Chip key={cr.childName} label={cr.childName} size="small"
                            color={cr.needsAttention ? 'warning' : 'default'} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  );
}

export default function WeeklyReportsPage() {
  return (<FeatureGate feature="sibling_comparison" fallback={<UpgradePrompt feature="sibling_comparison" />}><WeeklyReportsContent /></FeatureGate>);
}
